import { useTranslation } from 'react-i18next';
import React from 'react';
import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import BrandMark from 'components/common/BrandMark';
import LanguageSwitcher from 'i18n/LanguageSwitcher';
import { useTokens } from 'theme';

// The landing's display family, for headlines only.
export const DISPLAY = "'Unbounded', 'Manrope', system-ui, sans-serif";

/**
 * Sign-in and invite: the dark brand panel on the left (a faint grid, the
 * mark, the headline) and the page-coloured form column on the right. The
 * panel folds away on narrow screens and the mark moves above the form.
 */
export default function AuthLayout({ title, subtitle, children }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const grid = 'rgba(255,255,255,0.05)';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: t.canvas,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: t.chrome,
          backgroundImage: `linear-gradient(${grid} 1px, transparent 1px), linear-gradient(90deg, ${grid} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
          px: 5,
          py: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BrandMark size={22} tone="light" />
          <Typography sx={{ color: t.onChrome, fontWeight: 800, fontSize: '1.05rem' }}>
            Evolve
          </Typography>
        </Box>

        <Box sx={{ my: 'auto', maxWidth: 520 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: DISPLAY,
              fontWeight: 800,
              fontSize: 'clamp(2rem, 3.3vw, 3rem)',
              lineHeight: 1.12,
              letterSpacing: '-0.02em',
              color: t.onChrome,
              mb: 3,
            }}
          >{translate('shell.brandHeadline')}{' '}
            <Box component="span" sx={{ display: 'block', color: t.onChromeAccent }}>{translate('shell.brandHeadlineAccent')}</Box>
          </Typography>
          <Typography sx={{ color: t.onChromeMuted, fontSize: '0.95rem', lineHeight: 1.6, maxWidth: 380 }}>{translate('shell.brandDescription')}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 5 } }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}><LanguageSwitcher /></Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mb: 5 }}>
            <BrandMark size={24} tone="ink" />
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>Evolve</Typography>
          </Box>

          <Typography
            component="h2"
            sx={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: '1.75rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 1 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.9rem', color: t.inkMuted, mb: 3.5 }}>
              {subtitle}
            </Typography>
          )}

          {children}
        </Box>
      </Box>
    </Box>
  );
}

/** A label above its field, with an optional link on the right. */
export function FieldLabel({ htmlFor, children, aside }) {
  useTranslation();
  const t = useTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
      <Typography component="label" htmlFor={htmlFor} sx={{ fontSize: '0.85rem', fontWeight: 600, color: t.ink }}>
        {children}
      </Typography>
      {aside}
    </Box>
  );
}

/** The eye at the end of a password field. */
export function PasswordToggle({ shown, onToggle }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  return (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        aria-label={shown ? translate('shell.hidePassword') : translate('shell.showPassword')}
        edge="end"
        size="small"
        sx={{ color: t.iconMuted, '&:hover': { color: t.ink } }}
      >
        {shown ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );
}
